import { t } from '@procumi/shared';
import { useState } from 'react';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';

/**
 * „Securitate” în „Profilul meu” (2026-11-03): schimbarea parolei din cont, fără mail, și ieșirea de
 * pe celelalte dispozitive (telefonul pierdut, calculatorul de la birou lăsat deschis).
 */
export function SecuritySection({ toast }: { toast: Toast }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const reset = () => {
    setOpen(false);
    setCurrent('');
    setNext('');
    setAgain('');
    setShow(false);
    setProblem(null);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setProblem(null);
    if (next.length < 8) return setProblem(t('Parola trebuie să aibă cel puțin 8 caractere'));
    if (next !== again) return setProblem(t('Cele două parole noi nu sunt la fel.'));
    setBusy(true);
    try {
      const r = await api.changePassword(current, next);
      reset();
      toast(
        r.ended
          ? t('Parola e schimbată. Am scos contul de pe celelalte {0} dispozitive.', r.ended)
          : t('Parola e schimbată.'),
      );
    } catch (err) {
      setProblem(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const endOthers = async () => {
    setBusy(true);
    try {
      const r = await api.endOtherSessions();
      toast(
        r.ended
          ? t('Am scos contul de pe celelalte {0} dispozitive.', r.ended)
          : t('Nu erai intrat de pe alte dispozitive.'),
      );
    } catch (err) {
      toast(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const type = show ? 'text' : 'password';
  return (
    <section className="sec-sec" aria-labelledby="sec-sec-t">
      <h3 id="sec-sec-t">{t('Securitate')}</h3>
      {open ? (
        <form className="sec-form" onSubmit={(e) => void save(e)}>
          <label className="f" htmlFor="pw-current">
            {t('Parola de acum')}
            <input
              id="pw-current"
              className="inp"
              type={type}
              value={current}
              autoComplete="current-password"
              onChange={(e) => setCurrent(e.target.value)}
            />
          </label>
          <label className="f" htmlFor="pw-next">
            {t('Parola nouă')}
            <input
              id="pw-next"
              className="inp"
              type={type}
              value={next}
              autoComplete="new-password"
              onChange={(e) => setNext(e.target.value)}
            />
            <span className="hint" style={{ margin: 0 }}>
              {t('Cel puțin 8 caractere.')}
            </span>
          </label>
          <label className="f" htmlFor="pw-again">
            {t('Parola nouă, încă o dată')}
            <input
              id="pw-again"
              className="inp"
              type={type}
              value={again}
              autoComplete="new-password"
              onChange={(e) => setAgain(e.target.value)}
            />
          </label>
          <label className="check-line">
            <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
            <span>{t('Arată parolele')}</span>
          </label>
          {problem ? (
            <div className="auth-err" role="alert">
              {problem}
            </div>
          ) : null}
          <p className="muted" style={{ margin: 0 }}>
            {t('După schimbare, ieși din cont de pe celelalte dispozitive. Aici rămâi intrat.')}
          </p>
          <div className="profile-acts">
            <button className="btn primary" type="submit" disabled={busy || !current || !next || !again}>
              {busy ? t('Se salvează…') : t('Schimbă parola')}
            </button>
            <button className="btn" type="button" disabled={busy} onClick={reset}>
              {t('Renunță')}
            </button>
          </div>
        </form>
      ) : (
        <div className="profile-acts">
          <button className="btn" type="button" disabled={busy} onClick={() => setOpen(true)}>
            {t('Schimbă parola')}
          </button>
          <button className="btn" type="button" disabled={busy} onClick={() => void endOthers()}>
            {t('Ieși de pe celelalte dispozitive')}
          </button>
        </div>
      )}
    </section>
  );
}
